import { useEffect, useState } from "react";
import { Tooltip, TextField, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { toast } from "react-toastify";
import TableLayout from "../../../components/layout/TableLayout";
import {
  NoRecordsFound,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/Table";
import useModal from "../../../hooks/useModal";
import useConfirm from "../../../hooks/useConfirm";
import useAuthUser from "../../../hooks/useAuthUser";
import Button from "../../../components/ui/Button";
import {
  getGrievanceCategoryManageList,
  createGrievanceCategory,
  updateGrievanceCategory,
  deleteGrievanceCategory,
} from "../../../services/grievance";

// Add/Edit form for both a top-level Category and a Subcategory — which one
// it creates/edits depends on whether a parent is selected.
const CategoryForm = ({ categories, existing, defaultParentId, onSaved }) => {
  const { hideModal } = useModal();
  const isEdit = Boolean(existing);
  const [name, setName] = useState(existing?.name || "");
  const [parentId, setParentId] = useState(
    existing?.parent_id || defaultParentId || ""
  );
  const [isActive, setIsActive] = useState(
    existing ? Boolean(Number(existing.is_active)) : true
  );
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // A category that already has subcategories under it can't be turned into
  // a subcategory itself — keep the parent dropdown locked in that case.
  const parentLocked = isEdit && !existing.parent_id && existing.subcategories?.length > 0;

  const handleSubmit = async () => {
    if (!name.trim()) {
      setErrors({ name: "Name is required." });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        parent_id: parentId || null,
        is_active: isActive,
      };

      if (isEdit) {
        await updateGrievanceCategory(existing.id, payload);
        toast.success("Saved successfully");
      } else {
        await createGrievanceCategory(payload);
        toast.success("Added successfully");
      }
      onSaved();
      hideModal();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-2">
      <TextField
        fullWidth
        variant="standard"
        label="Name"
        className="mb-3"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setErrors((prev) => ({ ...prev, name: "" }));
        }}
        error={!!errors.name}
        helperText={errors.name}
      />

      <TextField
        select
        fullWidth
        variant="standard"
        label="Parent Category"
        className="mb-3"
        value={parentId}
        onChange={(e) => setParentId(e.target.value)}
        disabled={parentLocked}
        helperText={
          parentLocked
            ? "This category already has subcategories, so it can't be nested."
            : "Leave blank for a top-level category."
        }
      >
        <MenuItem value="">— None (top-level category) —</MenuItem>
        {categories
          .filter((c) => !existing || c.id !== existing.id)
          .map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
      </TextField>

      <TextField
        select
        fullWidth
        variant="standard"
        label="Status"
        className="mb-3"
        value={isActive ? "1" : "0"}
        onChange={(e) => setIsActive(e.target.value === "1")}
      >
        <MenuItem value="1">Active</MenuItem>
        <MenuItem value="0">Inactive</MenuItem>
      </TextField>

      <div className="d-flex justify-content-end mt-3 gap-2">
        <Button variant="secondary" onClick={() => hideModal()} disabled={loading}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={loading}>
          {isEdit ? "Update" : "Add"}
        </Button>
      </div>
    </div>
  );
};

const StatusPill = ({ isActive }) => (
  <span
    style={{
      display: "inline-block",
      fontSize: "11.5px",
      fontWeight: 600,
      padding: "2px 10px",
      borderRadius: "20px",
      background: isActive ? "#e6f7ee" : "#fdecec",
      color: isActive ? "#219653" : "#b83227",
    }}
  >
    {isActive ? "Active" : "Inactive"}
  </span>
);

const GrievanceCategorySetting = () => {
  const { showModal } = useModal();
  const { confirm } = useConfirm();
  const { isAdminHrRole } = useAuthUser();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = async () => {
    try {
      const res = await getGrievanceCategoryManageList();
      setCategories(res?.data?.data || []);
    } catch (error) {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddCategory = () => {
    showModal(
      "Add Category",
      <CategoryForm categories={categories} onSaved={fetchCategories} />
    );
  };

  const handleAddSubcategory = (parent) => {
    showModal(
      "Add Subcategory",
      <CategoryForm
        categories={categories}
        defaultParentId={parent.id}
        onSaved={fetchCategories}
      />
    );
  };

  const handleEdit = (existing) => {
    showModal(
      existing.parent_id ? "Edit Subcategory" : "Edit Category",
      <CategoryForm
        categories={categories}
        existing={existing}
        onSaved={fetchCategories}
      />
    );
  };

  const handleDelete = async (row) => {
    const isConfirmed = await confirm({
      title: "Delete",
      content: `Are you sure you want to delete "${row.name}"?`,
      confirmText: "Yes, Delete",
      cancelText: "Cancel",
    });
    if (!isConfirmed) return;

    try {
      await deleteGrievanceCategory(row.id);
      toast.success("Deleted successfully");
      fetchCategories();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete");
    }
  };

  if (!isAdminHrRole) return null;

  // Flatten the parent/child tree into rows for a single table — each
  // subcategory rendered right under its parent, visually indented.
  const rows = categories.flatMap((category) => [
    { ...category, isSub: false },
    ...(category.subcategories || []).map((sub) => ({
      ...sub,
      isSub: true,
      parentName: category.name,
    })),
  ]);

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h2 style={{ marginBottom: 0 }}>Grievance Setting</h2>
        <Button onClick={handleAddCategory}>
          <AddIcon sx={{ fontSize: 18 }} className="me-1" />
          Add Category
        </Button>
      </div>

      <TableLayout>
        <Table className="table-col-freeze">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Parent Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="last-col-freeze">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && rows.length > 0 ? (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.isSub && (
                      <SubdirectoryArrowRightIcon
                        sx={{ fontSize: 16, color: "#999", marginRight: "4px", verticalAlign: "middle" }}
                      />
                    )}
                    {row.name}
                  </TableCell>
                  <TableCell>{row.isSub ? "Subcategory" : "Category"}</TableCell>
                  <TableCell>{row.parentName || "--"}</TableCell>
                  <TableCell>
                    <StatusPill isActive={Number(row.is_active) === 1} />
                  </TableCell>
                  <TableCell className="last-col-freeze">
                    {!row.isSub && (
                      <Tooltip title="Add Subcategory" placement="top" arrow>
                        <AddIcon
                          className="cursor-pointer me-2"
                          onClick={() => handleAddSubcategory(row)}
                        />
                      </Tooltip>
                    )}
                    <Tooltip title="Edit" placement="top" arrow>
                      <CreateOutlinedIcon
                        className="cursor-pointer me-2"
                        onClick={() => handleEdit(row)}
                      />
                    </Tooltip>
                    <Tooltip title="Delete" placement="top" arrow>
                      <DeleteOutlineIcon
                        className="cursor-pointer"
                        color="error"
                        onClick={() => handleDelete(row)}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <NoRecordsFound colSpan={5} />
            )}
          </TableBody>
        </Table>
      </TableLayout>
    </div>
  );
};

export default GrievanceCategorySetting;
